import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  CONSTRAINT_TYPE_LABELS,
  SEVERITY_LABELS,
  SOURCE_LABELS,
  humanizeConstraint,
} from "@/lib/constraints/humanize";
import { formatDistanceToNow } from "date-fns";

export interface ConstraintRow {
  id: string;
  constraint_type: string;
  parameters: Record<string, any>;
  severity: string;
  status: string;
  source: string;
  team_id: string | null;
  response_notes: string | null;
  partial_accommodation: any;
  created_by: string | null;
  reviewed_by: string | null;
  created_at: string;
  reviewed_at: string | null;
  updated_at: string | null;
}

interface Props {
  constraint: ConstraintRow;
  teamName: string | null;
  opponentNameLookup: (teamId: string) => string | undefined;
  onClick: (c: ConstraintRow) => void;
}

const SEVERITY_COLORS: Record<string, string> = {
  hard: "border-red-300 text-red-700 bg-red-50",
  soft: "border-amber-300 text-amber-700 bg-amber-50",
  preference: "border-gray-300 text-gray-600 bg-gray-50",
};

const SOURCE_COLORS: Record<string, string> = {
  coach_request: "bg-blue-100 text-blue-800",
  operator: "bg-slate-100 text-slate-700",
  system: "bg-purple-100 text-purple-700",
};

export function ConstraintCard({
  constraint,
  teamName,
  opponentNameLookup,
  onClick,
}: Props) {
  const severityColor = SEVERITY_COLORS[constraint.severity] || SEVERITY_COLORS.preference;
  const sourceColor = SOURCE_COLORS[constraint.source] || SOURCE_COLORS.system;
  const timeAgo = formatDistanceToNow(new Date(constraint.created_at), { addSuffix: true });

  return (
    <Card
      className="cursor-pointer hover:border-primary-300 hover:shadow-sm transition-all"
      onClick={() => onClick(constraint)}
    >
      <CardContent className="p-3 space-y-1.5">
        {/* Top row: team name + severity */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold truncate">
            {teamName || "Event-wide"}
          </span>
          <Badge variant="outline" className={`text-xs shrink-0 ${severityColor}`}>
            {SEVERITY_LABELS[constraint.severity] || constraint.severity}
          </Badge>
        </div>

        {/* Type label */}
        <p className="text-xs font-medium text-muted-foreground">
          {CONSTRAINT_TYPE_LABELS[constraint.constraint_type] || constraint.constraint_type}
        </p>

        {/* Humanized parameters */}
        <p className="text-sm">
          {humanizeConstraint(constraint, { opponentNameLookup })}
        </p>

        {/* Bottom row: source + timestamp */}
        <div className="flex items-center justify-between pt-1">
          <Badge variant="secondary" className={`text-xs ${sourceColor}`}>
            {SOURCE_LABELS[constraint.source] || constraint.source}
          </Badge>
          <span className="text-xs text-muted-foreground">{timeAgo}</span>
        </div>
      </CardContent>
    </Card>
  );
}
